import {
    IAccountBranding,
    IClient,
    IClientReport,
    ICorpPermissionsWithPalette,
    IDebt,
    ILoan,
    IMBSCorpPermissions,
    IProperty,
    IPurchaseDetail,
    IReport,
    LOAN_CATEGORY,
    navigationPermissionsDefault,
    notificationPermissionsDefault,
    REFI_TYPES,
    restrictionsPermissionsDefault,
} from "./types";
import { makeSudoGUID } from "./utils";

export const initialDebt: IDebt = {
    id: "",
    name: "",
    balance: 0,
    interest: 0,
    payment: 0,
    type: "Installment",
    included: false,
};

export const initialLoan: ILoan = {
    category: LOAN_CATEGORY.PURCHASE,
    isSecondary: false,
    propertyID: "",
    id: "",
    name: null,
    amount: null,
    purchaseDetailsID: null,
    downPayment: null,
    downPaymentPercentage: null,
    term: 30,
    program: "conventional",
    customProgram: "",
    type: "fixed",
    rate: 0,
    points: 0,
    MI: null,
    annualMIPercentage: null,
    upfrontMI: null,
    upfrontMIPercentage: null,
    buydownCosts: null,
    closingCosts: null,
    userPrepaidEscrows: null,
    escrows: { insurance: null, tax: null, interest: null },
    ccID: null,
    closingCostObj: null,
    ARM: {
        frequency: 12,
        scenario: "worst",
        initialCap: 5,
        adjustmentCap: 2,
        lifetimeCap: 5,
        customRates: [0],
    },
    secondLoan: {
        amount: null,
        term: 0,
        rate: 0,
        ltv: 0,
        type: "fixed",
    },
    customAPR: null,
    customAPRCost: null,
    MIWorksheet: {
        activeModifiers: [],
        isMonthly: true,
        isRefund: true,
        isFinanced: false,
    },
    didYouRefi: false,
    refiCost: 4000,
    startDate: { month: new Date().getMonth() + 1, year: new Date().getFullYear() },
    userMonthlyPayment: null,
    frontRatioMax: 33,
    backRatioMax: 43,
    targetLTV: 75,
    maxDownPayment: null,
    userCashToApply: null,
    hasCashApplied: false,
    userBalance: null,
    selfInsured: false,
    additionalMonthlyPayment: 0,
    additionalMonthlyPayments: [],
    useAdditionalMonthlyPayment: true,
    userAdditionalMonthlyPayment: null,
    isFinancedCC: false,
    isFinancedPrepaidEscrow: false,
    isFinancedPoints: false,
    lumpPayments: [],
    creditsObj: null,
    additionalCredits: null,
    cowWaiting: 1,
    cowLoans: [],
    hidden: false,
    estimatedHomeValue: null,
    applyAvailableCashBeforeDebtsToDownPayment: false,
    refiType: REFI_TYPES.LOOSE_LTV,
    LTVLimit: 75,
    investment: {
        startingBalance: 0,
        rate: 0,
        periodPayment: [],
    },
};

export const initialPurchaseDetail: IPurchaseDetail = {
    id: makeSudoGUID(),
    price: 0,
    monthlyHomeInsurance: 0,
    userMonthlyHomeInsurance: null,
    userTaxRate: null,
    bidOver: 0,
};

export const initialProperty: IProperty = {
    id: makeSudoGUID(),
    name: "",
    purchaseDetails: [initialPurchaseDetail],
    countyTaxRate: 0,
    monthlyAssociationFee: 0,
    county: "",
    countyType: "",
    fip: "",
    city: "",
    state: "",
    zip: "",
    address: "",
    addressDisplay: null,
    appreciationType: "forecast",
    appreciationTypes: ["forecast"],
    customAppreciation: 4,
    forecastData: [],
    historicalData: [],
    actualHistoricalData: [],
    medianHomePrice: 300000,
    streetNumber: "",
    route: "",
    apartment: "",
    latitude: "",
    longitude: "",
    userEstimatedValue: null,
    purchaseDate: { month: 0, year: 0 },
    costToSell: 4,
    image: "",
    monthlyAllow: 0,
    repairs: 0,
    initialRenovationCost: 0,
    taxInc: 2,
    monthlyRent: 0,
    rentIns: 0,
    rentInc: 0,
    propertyManagementFee: 0,
    avgAnnualVacancy: 0,
    rentSqFt: 0,
    buySqFt: 0,
    userAppraisedValue: null,
    userMonthlyRent: null,
};

export const initialReport: IReport = {
    id: "",
    toolName: "",
    cobrandID: null,
    cobrandPosition: null,
    lastUpdated: null,
    lastSent: null,
    lastViewed: null,
    toolMeta: {},
    properties: [],
    loans: [],
    debts: [],
};

export const initialClientReport: IClientReport = {
    id: "",
    toolName: "",
    cobrandID: null,
    cobrandPosition: null,
    lastViewed: null,
    customMessage: "",
    lastSent: null,
    lastUpdated: null,
    toolMeta: {},
    loans: [],
    properties: [],
    debts: [],
};

export const initialClient: IClient = {
    _id: "",
    userID: "",
    name: "",
    primaryEmail: "",
    secondaryEmails: [],
    ficoMin: 720,
    DTI: 0,
    taxRate: 22,
    monthlyIncome: 0,
    filingStatus: "marriedJoint",
    loans: [],
    properties: [],
    reports: [],
    debts: [],
    reinvestment: [],
    version: 1,
    createdAt: new Date(),
    updatedAt: new Date(),
};
export const initialBranding: IAccountBranding = {
    userID: "",
    headshotUrl: "",
    license: { type: "nmls", number: "" },
    phoneNumbers: [{ type: "office" }],
    disclaimerText: "",
    logoUrl: "",
    websiteUrl: "",
    company: {
        name: "",
        address: "",
        city: "",
        state: "",
        zipCode: "",
        complianceLogos: {
            memberFdic: false,
            equalHousingLender: false,
            equalHousingOpportunity: false,
            nationalCreditUnionAdministration: false,
        },
    },
};

export const initialMBSCorpPermissions: IMBSCorpPermissions = {
    corpID: "",
    notifications: { ...notificationPermissionsDefault },
    navigation: { ...navigationPermissionsDefault },
    restrictions: { ...restrictionsPermissionsDefault },
};

export const initialMBSCorpPermissionsWithPalettes: ICorpPermissionsWithPalette = {
    ...initialMBSCorpPermissions,
    palettes: [],
};
